import React, { useState } from "react";
import { Button, Modal, Form, Spinner, Alert } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faEnvelope, faPhone, faIdCard, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { postApi } from "../redux/api";
import { useDispatch, useSelector } from "react-redux";
import { LOGIN } from "../redux/actions/actionTypes";
import { toast } from "react-hot-toast";

function ProfileCompletionModal() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  const [form, setForm] = useState({
    email: user?.email || "",
    phone: user?.phone || "",
    designation: user?.designation || "",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const isComplete = user?.email && user?.phone;

  const validate = () => {
    const newErrors = {};
    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!form.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^0\d{10}$/.test(form.phone)) {
      newErrors.phone = "Enter a valid Nigerian phone number (11 digits starting with 0)";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = ({ target: { name, value } }) => {
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const resp = await postApi("treasury-app/update_contact_info", {
        email: form.email,
        phone: form.phone,
        designation: form.designation,
        username: user.username,
        fullname: user.fullname || "",
      });
      if (resp.success) {
        dispatch({
          type: LOGIN,
          payload: { ...user, email: form.email, phone: form.phone, designation: form.designation },
        });
        setSaved(true);
        toast.success("Profile updated successfully!");
      } else {
        toast.error(resp.msg || "Failed to update profile");
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (isComplete) return null;

  return (
    <Modal
      show={true}
      backdrop="static"
      keyboard={false}
      centered
      size="md"
    >
      <Modal.Header>
        <Modal.Title className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faUser} className="text-primary" />
          Complete Your Profile
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body>
          {saved ? (
            <Alert variant="success" className="mb-0">
              <strong>Profile updated!</strong> You can now proceed.
            </Alert>
          ) : (
            <>
              <p className="text-muted mb-3">
                Please provide your contact information before proceeding.
              </p>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  <FontAwesomeIcon icon={faEnvelope} className="me-2 text-muted" />
                  Email Address <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  isInvalid={!!errors.email}
                  placeholder="you@example.com"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  <FontAwesomeIcon icon={faPhone} className="me-2 text-muted" />
                  Phone Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  isInvalid={!!errors.phone}
                  placeholder="08031234567"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.phone}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Nigerian mobile number — 11 digits starting with 0
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  <FontAwesomeIcon icon={faIdCard} className="me-2 text-muted" />
                  Job Title / Designation
                </Form.Label>
                <Form.Control
                  type="text"
                  name="designation"
                  value={form.designation}
                  onChange={handleChange}
                  placeholder="e.g. Accountant, Director, etc."
                />
              </Form.Group>

              {Object.keys(errors).length > 0 && (
                <Alert variant="danger" className="py-2 mb-0">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                  Please fix the errors above.
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" type="submit" disabled={saving || saved}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Saving...
              </>
            ) : saved ? (
              "Continue"
            ) : (
              "Save Profile"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default ProfileCompletionModal;
